import { User } from 'types/User';

type UserPasswordError =
  | 'personal-data'
  | 'char-sequence'
  | 'char-repeated'
  | 'short-password'
  | 'too-long-password'
  | 'diff-characters';

export const USER_PASSWORD_MAX_LENGTH = 255;
const USER_PASSWORD_MIN_LENGTH = 20;
const USER_PASSWORD_MIN_DIFF_CHARS = 8;
const USER_PASSWORD_MIN_SEQUENCE_CHARS = 4;

const ALPHABET_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
const ALPHABET_LETTERS_REVERSE = ALPHABET_LETTERS.split('').reverse().join('');
const NUMBERS = '0123456789';
const NUMBERS_REVERSE = NUMBERS.split('').reverse().join('');

const findSubstring = (value: string, insideOf: string, substrLength: number) => {
  return Array.from({ length: insideOf.length - substrLength + 1 }).some((_, i) =>
    value.toLowerCase().includes(insideOf.substring(i, i + substrLength))
  );
};

export const validateUserPassword = (
  password: string,
  user: Partial<Pick<User, 'email' | 'firstName' | 'lastName'>>
) => {
  const errors: Record<UserPasswordError, string | null> = {
    'personal-data': null,
    'char-sequence': null,
    'char-repeated': null,
    'short-password': null,
    'too-long-password': null,
    'diff-characters': null,
  };

  if (password.length > USER_PASSWORD_MAX_LENGTH) {
    errors[
      'too-long-password'
    ] = `Your password cannot contain more than ${USER_PASSWORD_MAX_LENGTH} characters.`;
  }

  if (password.length < USER_PASSWORD_MIN_LENGTH) {
    errors['short-password'] = `Your password must contain at least ${USER_PASSWORD_MIN_LENGTH} characters.`;
  }

  if (
    (!!user.email?.trim() && password.toLowerCase().includes(user.email.trim().toLowerCase())) ||
    (!!user.firstName?.trim() && password.toLowerCase().includes(user.firstName.trim().toLowerCase())) ||
    (!!user.lastName?.trim() && password.toLowerCase().includes(user.lastName.trim().toLowerCase()))
  ) {
    errors['personal-data'] = 'Do not use personal information like your email or name in your password.';
  }

  if (new Set(password.toLowerCase().split('')).size < USER_PASSWORD_MIN_DIFF_CHARS) {
    errors[
      'diff-characters'
    ] = `Your password must contain at least ${USER_PASSWORD_MIN_DIFF_CHARS} different characters.`;
  }

  if (/(.)\1\1\1/.test(password.toLowerCase())) {
    errors['char-repeated'] = `Do not use 4 or more consecutive repeated characters like "1111" or "aaaa".`;
  }

  if (
    findSubstring(password, ALPHABET_LETTERS, USER_PASSWORD_MIN_SEQUENCE_CHARS) ||
    findSubstring(password, ALPHABET_LETTERS_REVERSE, USER_PASSWORD_MIN_SEQUENCE_CHARS) ||
    findSubstring(password, NUMBERS, USER_PASSWORD_MIN_SEQUENCE_CHARS) ||
    findSubstring(password, NUMBERS_REVERSE, USER_PASSWORD_MIN_SEQUENCE_CHARS)
  ) {
    errors[
      'char-sequence'
    ] = `Do not use sequences of ${USER_PASSWORD_MIN_SEQUENCE_CHARS} or more characters like "1234", "9876" or "abcd".`;
  }

  return {
    errors,
    firstErrorMessage:
      errors['too-long-password'] ||
      errors['short-password'] ||
      errors['personal-data'] ||
      errors['diff-characters'] ||
      errors['char-repeated'] ||
      errors['char-sequence'],
    hasErrors: Object.values(errors).some((v) => !!v),
  };
};
